import { useCallback, useEffect, useState } from 'react';
import { ApiError, api, formatDateId } from '../api/client';
import { Alert, Button, Card, Field, Input, Modal, PasswordInput, Spinner } from '../components/ui';
import { useConfirm } from '../components/confirm';
import { useT } from '../i18n/locale';
import { PLATFORM_ROLE_LABEL, PlatformRole, useAdminSession } from './session';

interface Row {
  id: string;
  email: string;
  name: string;
  role: PlatformRole;
  is_active: boolean;
  must_change_password: boolean;
  last_login_at: string | null;
  created_at: string;
}

export default function AdminsScreen() {
  const { admin: me } = useAdminSession();
  const confirm = useConfirm();
  const t = useT();

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Row | null>(null);
  const [resetting, setResetting] = useState<Row | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows((await api.get<{ admins: Row[] }>('/platform/admins')).admins);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('admin.adminsLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function update(r: Row, patch: Record<string, unknown>, message: string) {
    setBusyId(r.id);
    setError(null);
    setNotice(null);
    try {
      await api.patch(`/platform/admins/${r.id}`, patch);
      setNotice(message);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('common.saveFailed'));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="mx-auto w-full max-w-5xl p-4">
      {error ? <Alert tone="error">{error}</Alert> : null}
      {notice ? <Alert tone="success">{notice}</Alert> : null}

      <Card className="mb-3 flex items-center justify-between gap-3 p-4">
        <div>
          <h2 className="m-0 text-base font-bold text-slate-800">{t('admin.adminsHeading')}</h2>
          <p className="m-0 mt-0.5 text-sm text-slate-500">{t('admin.adminsIntro')}</p>
        </div>
        <Button onClick={() => setAdding(true)}>{t('admin.addAdmin')}</Button>
      </Card>

      <Card className="overflow-hidden">
        {loading ? (
          <Spinner label={t('common.loading')} />
        ) : (
          <table className="w-full border-collapse text-left">
            <thead className="border-b border-slate-200 bg-slate-100/60 text-[11px] font-bold tracking-wider text-slate-500 uppercase">
              <tr>
                <th className="px-4 py-2">{t('team.colName')}</th>
                <th className="px-3 py-2">{t('team.colEmail')}</th>
                <th className="w-40 px-3 py-2">{t('team.colRole')}</th>
                <th className="px-3 py-2">{t('team.colLastLogin')}</th>
                <th className="px-4 py-2 text-right">{t('team.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isMe = r.id === me?.id;
                return (
                  <tr
                    key={r.id}
                    className={`border-b border-slate-100 ${r.is_active ? '' : 'bg-slate-50 text-slate-400'}`}
                  >
                    <td className="px-4 py-2 text-sm font-bold text-slate-900">
                      {r.name}
                      {isMe ? (
                        <span className="ml-1 text-xs text-slate-400">{t('common.you')}</span>
                      ) : null}
                      {r.must_change_password ? (
                        <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                          {t('admin.mustChangeBadge')}
                        </span>
                      ) : null}
                      {r.is_active ? null : (
                        <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">
                          {t('common.inactive')}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-600">{r.email}</td>
                    <td className="px-3 py-2">
                      {/*
                        Your own row is read-only. The server refuses a
                        self-change outright, because a superadmin who demoted
                        themselves could leave the platform with no way back in
                        except hand-written SQL.
                      */}
                      {isMe ? (
                        <span className="text-sm font-semibold text-slate-700">
                          {t(PLATFORM_ROLE_LABEL[r.role])}
                        </span>
                      ) : (
                        <select
                          value={r.role}
                          disabled={busyId === r.id}
                          onChange={(e) =>
                            void update(
                              r,
                              { role: e.target.value },
                              t('admin.roleChanged', { name: r.name }),
                            )
                          }
                          className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm outline-none focus:border-blue-500"
                        >
                          {Object.entries(PLATFORM_ROLE_LABEL).map(([v, label]) => (
                            <option key={v} value={v}>
                              {t(label)}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-500">
                      {r.last_login_at ? formatDateId(r.last_login_at) : t('common.never')}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap justify-end gap-1">
                        {/* Name and email are ordinary corrections, so they
                            are editable even on your own row. */}
                        <Button variant="ghost" disabled={busyId === r.id} onClick={() => setEditing(r)}>
                          {t('common.edit')}
                        </Button>
                        {isMe ? null : (
                          <>
                            <Button
                              variant="ghost"
                              disabled={busyId === r.id}
                              onClick={() => setResetting(r)}
                            >
                              {t('team.resetPassword')}
                            </Button>
                            <Button
                              variant={r.is_active ? 'danger' : 'ghost'}
                              disabled={busyId === r.id}
                              onClick={async () => {
                                if (r.is_active) {
                                  const ok = await confirm({
                                    title: t('admin.deactivateTitle', { name: r.name }),
                                    tone: 'danger',
                                    confirmLabel: t('team.deactivate'),
                                    body: t('admin.deactivateBody'),
                                  });
                                  if (!ok) return;
                                }
                                await update(
                                  r,
                                  { isActive: !r.is_active },
                                  r.is_active
                                    ? t('admin.adminDeactivated')
                                    : t('admin.adminActivated'),
                                );
                              }}
                            >
                              {r.is_active ? t('team.deactivate') : t('team.activate')}
                            </Button>
                            {/* Deleting is offered last and framed as the
                                lesser-used option: deactivating keeps the
                                account's history attached to a live row. */}
                            <Button
                              variant="danger"
                              disabled={busyId === r.id}
                              onClick={async () => {
                                const ok = await confirm({
                                  title: t('admin.deleteAdminTitle', { name: r.name }),
                                  tone: 'danger',
                                  confirmLabel: t('admin.deletePermanent'),
                                  body: t('admin.deleteAdminBody'),
                                  details: [
                                    { label: t('common.email'), value: r.email },
                                    { label: t('team.colRole'), value: t(PLATFORM_ROLE_LABEL[r.role]) },
                                  ],
                                });
                                if (!ok) return;
                                setBusyId(r.id);
                                setError(null);
                                setNotice(null);
                                try {
                                  await api.delete(`/platform/admins/${r.id}`);
                                  setNotice(t('admin.adminDeleted', { name: r.name }));
                                  await load();
                                } catch (e) {
                                  setError(e instanceof ApiError ? e.message : t('admin.deleteFailed'));
                                } finally {
                                  setBusyId(null);
                                }
                              }}
                            >
                              {t('common.delete')}
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {editing ? (
        <EditAdmin
          admin={editing}
          onClose={() => setEditing(null)}
          onSaved={(name) => {
            setEditing(null);
            setNotice(t('admin.userDataSaved', { name }));
            void load();
          }}
        />
      ) : null}

      {resetting ? (
        <ResetPassword
          admin={resetting}
          onClose={() => setResetting(null)}
          onDone={(name) => {
            setResetting(null);
            setNotice(t('admin.passwordResetMustChange', { name }));
            void load();
          }}
        />
      ) : null}

      {adding ? (
        <AddAdmin
          onClose={() => setAdding(false)}
          onCreated={(name) => {
            setAdding(false);
            setNotice(t('admin.adminAddedNote', { name }));
            void load();
          }}
        />
      ) : null}
    </div>
  );
}

function AddAdmin({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (name: string) => void;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<string>(PlatformRole.Admin);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post('/platform/admins', { name, email, password, role });
      onCreated(name);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('admin.addAdminFailed'));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={t('admin.addAdminTitle')}
      onClose={onClose}
      dismissable={!busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={busy || !name || !email || !password}>
            {busy ? t('team.adding') : t('common.add')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error ? <Alert tone="error">{error}</Alert> : null}
        <Field label={t('team.colName')}>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label={t('common.email')}>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label={t('team.initialPassword')} hint={t('admin.initialPasswordHint')}>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
        </Field>
        <Field label={t('team.colRole')}>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
          >
            {Object.entries(PLATFORM_ROLE_LABEL).map(([v, label]) => (
              <option key={v} value={v}>
                {t(label)}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </Modal>
  );
}

function EditAdmin({
  admin,
  onClose,
  onSaved,
}: {
  admin: Row;
  onClose: () => void;
  onSaved: (name: string) => void;
}) {
  const t = useT();
  const [name, setName] = useState(admin.name);
  const [email, setEmail] = useState(admin.email);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unchanged = name.trim() === admin.name && email.trim() === admin.email;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/platform/admins/${admin.id}`, { name: name.trim(), email: email.trim() });
      onSaved(name.trim());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('common.saveFailed'));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={t('admin.editAdminTitle')}
      onClose={onClose}
      dismissable={!busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={busy || unchanged || name.trim().length < 2 || !email.trim()}
          >
            {busy ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error ? <Alert tone="error">{error}</Alert> : null}
        <Field label={t('team.colName')}>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label={t('common.email')} hint={t('admin.adminEmailHint')}>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function ResetPassword({
  admin,
  onClose,
  onDone,
}: {
  admin: Row;
  onClose: () => void;
  onDone: (name: string) => void;
}) {
  const t = useT();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/platform/admins/${admin.id}/password`, { newPassword: password });
      onDone(admin.name);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('team.resetFailed'));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={t('team.resetTitle', { name: admin.name })}
      onClose={onClose}
      dismissable={!busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={() => void submit()} disabled={busy || !password}>
            {busy ? t('common.saving') : t('team.resetSubmit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error ? <Alert tone="error">{error}</Alert> : null}
        <Alert tone="warn">{t('admin.resetWarn', { name: admin.name })}</Alert>
        <Field label={t('common.newPassword')}>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            autoComplete="new-password"
          />
        </Field>
      </div>
    </Modal>
  );
}
